<template>
  <div class="issue-field">
    <div class="issue-field__wrapper">
      <FileUploader
        v-if="canEdit"
        fileType="attachment"
        :label="$t('issues.add_attachment')"
        :modifiers="['always-show']"
        multiple
        @update-blob="updateBlob"
        @update-reader="updateReader"
      />
      <div class="issue-field__header">
        <div
          id="document-count"
          class="issue-field__title typo-small-title"
        >
          {{
            pluralize(
              documentList.length,
              $t('issues.document'),
              $t('issues.documents')
            )
          }}
        </div>
      </div>

      <div class="issue__attachments">
        <div
          v-for="document in documentList"
          :key="document.id"
          class="issue__attachment"
        >
          <div class="issue__attachment-icon">
            <FileIcon :contentType="document.content_type" />
          </div>
          <div class="issue__attachment-name">
            {{ document.filename }}
          </div>
          <div class="issue__attachment-file-size typo-tiny">
            {{ document.file_size }}
          </div>
          <div class="issue__attachment-menu">
            <Dropdown
              :items="attachmentMenuItems(document)"
              placement="bottom-end"
              @click-item="setDialogTypeAndId(document.id, $event)"
            >
              <UseSvg
                class="icon"
                name="vertical_ellipsis"
              />
            </Dropdown>
          </div>
        </div>
        <div
          v-if="documentList.length === 0"
          class="issue-field__body"
        >
          <p class="typo-small-title">
            {{ $t('issues.no_documents') }}
          </p>
        </div>
      </div>
    </div>
  </div>
  <Dialog
    v-if="dialogIsOpen"
    :message="dialogData.text.message"
    :title="dialogData.text.title"
    @dismiss-dialog="dismissDialogAction"
  >
    <template #actions>
      <button
        aria-label="delete"
        class="button button--inverse"
        type="button"
        @click="dialogData.action"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import I18n from 'i18n-js';
import pluralize from '@/functions/pluralize';

import FileUploader from '@/components/generic/file_uploader.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import Dialog from '@/components/generic/dialog.vue';

import UseSvg from '@/components/generic/use_svg.vue';
import FileIcon from '@/components/generic/file_icon.vue';

const emit = defineEmits(['updateBlob', 'deleteFile']);

const props = defineProps({
  /** Adds or removes uploading and deleting capabilities to the component */
  canEdit: { type: Boolean, default: true },
  /** List of documents */
  documentList: { type: Array, default: () => [] },
});

const dialogIsOpen = ref(false);
const deletionId = ref(null);

/**
 * Returns the correct text and action for the dialog.
 */
const dialogData = computed(() => {
  return {
    text: {
      title: I18n.t('issues.confirm_delete_title', { type: I18n.t('issues.type_file') }),
      message: I18n.t('issues.confirm_delete_message'),
    },
    action: removeAttachmentItem,
  };
});

function attachmentMenuItems(attachment) {
  let menuItems = [];
  menuItems.push({
      label: I18n.t('general.download'),
      path: attachment.url,
      target: '_blank',
      modifiers: [(!attachment.url ? 'disabled' : null)]
    });

  if (props.canEdit) {
    menuItems.push({
      label: I18n.t('general.delete'),
      key: 'delete',
      modifiers: ['destructive']
    });
  }

  return menuItems;
}

function updateBlob(item) {
  emit('updateBlob', item);
}

function updateReader(item) {
  props.documentList.push(item);
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  deletionId.value = null;
}

/**
 * Toggles dialog,
 * stores the id used for deletion,
 * stores the type of dialog. eg. image, file, task
 */
function setDialogTypeAndId(id, { key }) {
  if (key === 'delete') {
    dialogIsOpen.value = true;
    deletionId.value = id;
  }
}

function removeAttachmentItem() {
  var index = props.documentList?.findIndex((item) => item.id === deletionId.value);
  if (index !== -1) props.documentList.splice(index, 1);
  emit('deleteFile', deletionId.value);
  dismissDialogAction();
}
</script>
